import React, { useEffect, useRef, useState } from 'react'
import { AlertTriangle, CheckCircle, MonitorCog, Palette, UserRound } from 'lucide-react'
import { listModels, type ModelOption } from '../services/models'
import { authClient } from '../lib/auth-client'
import { useIsMobile } from '../lib/useMediaQuery'
import { AppSettings } from '../App'
import { Dialog, DialogHeader } from '@astryxdesign/core/Dialog'
import { Avatar } from '@astryxdesign/core/Avatar'
import { DEFAULT_AVATAR_IMAGE } from '../lib/defaultAvatarImage'
import { Layout, LayoutContent, LayoutFooter, LayoutPanel, HStack } from '@astryxdesign/core/Layout'
import { VStack } from '@astryxdesign/core/VStack'
import { Heading } from '@astryxdesign/core/Heading'
import { Link } from '@astryxdesign/core/Link'
import { Text } from '@astryxdesign/core/Text'
import PillButton from './PillButton'
import { TextInput } from '@astryxdesign/core/TextInput'
import { TextArea } from '@astryxdesign/core/TextArea'
import { Slider } from '@astryxdesign/core/Slider'
import { Switch } from '@astryxdesign/core/Switch'
import { Selector } from '@astryxdesign/core/Selector'
import { FormLayout } from '@astryxdesign/core/FormLayout'
import { SegmentedControl, SegmentedControlItem } from '@astryxdesign/core/SegmentedControl'
import SettingsSectionNav from './SettingsSectionNav'
import { cn } from '@renderer/lib/utils'
import DefaultAvatarPicker from './DefaultAvatarPicker'
import { modelSelectorOptions } from '../lib/modelSelectorOptions'

export type SettingsSection = 'account' | 'profile' | 'model' | 'appearance'

interface SettingsPanelProps {
  settings: AppSettings
  profile: AuthorProfile | null
  initialSection?: SettingsSection
  onSaveSettings: (settings: AppSettings) => void
  onSaveProfile: (profile: {
    penName: string
    authorBlurb: string
    libraryPublic: boolean
  }) => Promise<AuthorProfile>
  onUploadAvatar: (file: File) => Promise<AuthorProfile>
  onRemoveAvatar: () => Promise<AuthorProfile>
  onSelectDefaultAvatar: (defaultAvatarId: string) => Promise<AuthorProfile>
  onClose: () => void
}

const SECTION_DETAILS: Record<SettingsSection, { title: string; description: string }> = {
  account: {
    title: 'Account',
    description: 'Update your account name and password.'
  },
  profile: {
    title: 'Profile',
    description: 'Manage the author identity readers see when you publish.'
  },
  model: {
    title: 'Model configuration',
    description: 'Choose the language model and tune how it generates your stories.'
  },
  appearance: {
    title: 'Appearance',
    description: 'Choose how Nine Nights looks on this device.'
  }
}

export default function SettingsPanel({
  settings,
  profile,
  initialSection = 'account',
  onSaveSettings,
  onSaveProfile,
  onUploadAvatar,
  onRemoveAvatar,
  onSelectDefaultAvatar,
  onClose
}: SettingsPanelProps): React.JSX.Element {
  const { data: session } = authClient.useSession()
  const isMobile = useIsMobile()
  const [activeSection, setActiveSection] = useState<SettingsSection>(initialSection)
  const [localSettings, setLocalSettings] = useState<AppSettings>({ ...settings })
  const [savedTheme, setSavedTheme] = useState<AppSettings['theme']>(settings.theme)
  const [displayName, setDisplayName] = useState(session?.user.name || '')
  const [penName, setPenName] = useState(profile?.penName || '')
  const [authorBlurb, setAuthorBlurb] = useState(profile?.authorBlurb || '')
  const [libraryPublic, setLibraryPublic] = useState(profile?.libraryPublic ?? true)
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [models, setModels] = useState<ModelOption[]>([])
  const [saving, setSaving] = useState(false)
  const [feedback, setFeedback] = useState<{
    status: 'success' | 'error'
    message: string
  } | null>(null)

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const [avatarBusy, setAvatarBusy] = useState(false)
  const [pickerOpen, setPickerOpen] = useState(false)
  const [hasDefaultAvatars, setHasDefaultAvatars] = useState(false)

  /**
   * Keep the file picker's dismissal from closing the whole settings dialog.
   *
   * Dismissing the OS file picker fires a *bubbling* `cancel` event on the input. Astryx's
   * Dialog is a native <dialog>, and the signal for its own Escape dismissal is also a
   * `cancel` event. The dialog cannot tell the two apart, so without this the picker's event
   * reaches it and Cancel on the picker tears down the entire modal.
   *
   * This has to be a native listener: React does not type `cancel` for input elements, so
   * there is no onCancel prop to use here. Attaching directly to the input means propagation
   * stops before the dialog ever sees it, while the dialog's own Escape dismissal — which
   * originates on the dialog itself — is untouched.
   *
   * Keyed on activeSection because the input only exists while the Profile section is open.
   */
  useEffect(() => {
    const input = fileInputRef.current
    if (!input) return
    const stopCancelPropagation = (event: Event): void => event.stopPropagation()
    input.addEventListener('cancel', stopCancelPropagation)
    return () => input.removeEventListener('cancel', stopCancelPropagation)
  }, [activeSection])

  // Probes the library so the button can stay hidden when an admin has not curated anything
  // yet — an empty modal would be a dead end. Failures are silent: the button simply does not
  // appear, and uploading a photo still works. Routed through an extra promise tick so a
  // rejection (including `window.api` not being available at all, e.g. in tests that render
  // this panel without the app's api-bridge loaded) always lands in `.catch` rather than
  // throwing synchronously out of the effect.
  //
  // Only gated on activeSection, not on hasDefaultAvatars: the button needs to be able to go
  // both ways (an admin can empty the library mid-session, not just populate it), so the guard
  // must not stop probing once it finds a non-empty library. This does mean the effect re-runs
  // once more right after the first probe flips hasDefaultAvatars (it's a dependency), doing a
  // second, redundant fetch — a known, separately-tracked inefficiency, not a loop: the state
  // only actually changes (and the effect only re-fires) when the fetched result differs from
  // what's already stored, so this settles after at most one extra round-trip per transition.
  useEffect(() => {
    if (activeSection !== 'profile') return
    let cancelled = false
    void Promise.resolve()
      .then(() => window.api.profiles.listDefaultAvatars())
      .then((list) => {
        if (!cancelled) setHasDefaultAvatars(list.length > 0)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [activeSection, hasDefaultAvatars])

  const handleAvatarFile = async (event: React.ChangeEvent<HTMLInputElement>): Promise<void> => {
    const file = event.target.files?.[0]
    // Reset immediately so picking the same file twice still fires a change event.
    event.target.value = ''
    if (!file) return
    setAvatarBusy(true)
    setFeedback(null)
    try {
      await onUploadAvatar(file)
      setFeedback({ status: 'success', message: 'Profile picture updated.' })
    } catch (err: any) {
      setFeedback({ status: 'error', message: err?.message || 'Could not upload that image.' })
    } finally {
      setAvatarBusy(false)
    }
  }

  const handleAvatarRemove = async (): Promise<void> => {
    setAvatarBusy(true)
    setFeedback(null)
    try {
      await onRemoveAvatar()
      setFeedback({ status: 'success', message: 'Profile picture removed.' })
    } catch (err: any) {
      setFeedback({ status: 'error', message: err?.message || 'Could not remove the picture.' })
    } finally {
      setAvatarBusy(false)
    }
  }

  const applyTheme = (theme: 'light' | 'dark' | 'system'): void => {
    const root = document.documentElement
    const resolvedTheme =
      theme === 'system'
        ? window.matchMedia('(prefers-color-scheme: dark)').matches
          ? 'dark'
          : 'light'
        : theme
    root.setAttribute('data-theme', resolvedTheme)
    root.style.colorScheme = resolvedTheme
    root.classList.toggle('dark', resolvedTheme === 'dark')
  }

  const handleTestConnection = async (): Promise<void> => {
    setFeedback(null)
    try {
      const modelList = await listModels()
      setModels(modelList)
      if (modelList.length === 0) {
        setFeedback({
          status: 'error',
          message: 'No models are available. Add a provider key to the server .env.'
        })
      } else {
        if (!modelList.some((model) => model.id === localSettings.selectedModel)) {
          setLocalSettings((previous) => ({ ...previous, selectedModel: modelList[0].id }))
        }
      }
    } catch (error: any) {
      setFeedback({
        status: 'error',
        message: error.message || 'Unable to load the available models.'
      })
    }
  }

  const handleClose = (): void => {
    applyTheme(savedTheme)
    onClose()
  }

  const handleSave = async (): Promise<void> => {
    setSaving(true)
    setFeedback(null)
    try {
      if (activeSection === 'account') {
        const trimmedName = displayName.trim()
        if (!trimmedName) throw new Error('Name cannot be empty.')

        if (trimmedName !== session?.user.name) {
          const { error } = await authClient.updateUser({ name: trimmedName })
          if (error) throw new Error(error.message || 'Unable to update your name.')
        }

        const wantsPasswordChange = Boolean(currentPassword || newPassword || confirmPassword)
        if (wantsPasswordChange) {
          if (!currentPassword || !newPassword || !confirmPassword) {
            throw new Error('Complete all password fields to change your password.')
          }
          if (newPassword.length < 8) {
            throw new Error('Your new password must be at least 8 characters.')
          }
          if (newPassword !== confirmPassword) {
            throw new Error('The new passwords do not match.')
          }
          const { error } = await authClient.changePassword({
            currentPassword,
            newPassword,
            revokeOtherSessions: false
          })
          if (error) throw new Error(error.message || 'Unable to change your password.')
          setCurrentPassword('')
          setNewPassword('')
          setConfirmPassword('')
        }

        setFeedback({ status: 'success', message: 'Account details updated.' })
      } else if (activeSection === 'profile') {
        const saved = await onSaveProfile({
          penName: penName.trim(),
          authorBlurb,
          libraryPublic
        })
        setPenName(saved.penName)
        setAuthorBlurb(saved.authorBlurb)
        setLibraryPublic(saved.libraryPublic)
        setFeedback({ status: 'success', message: 'Profile updated.' })
      } else {
        onSaveSettings(localSettings)
        if (activeSection === 'appearance') {
          applyTheme(localSettings.theme)
          setSavedTheme(localSettings.theme)
        }
        setFeedback({ status: 'success', message: 'Settings saved.' })
      }
    } catch (error: any) {
      setFeedback({ status: 'error', message: error.message || 'Unable to save changes.' })
    } finally {
      setSaving(false)
    }
  }

  useEffect(() => {
    setFeedback(null)
    if (activeSection !== 'model' || models.length > 0) return
    const timer = window.setTimeout(() => void handleTestConnection(), 0)
    return () => window.clearTimeout(timer)
    // The connection check intentionally runs only when the model section first opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSection])

  const sectionDetails = SECTION_DETAILS[activeSection]

  return (
    <Dialog
      isOpen
      onOpenChange={(open) => {
        if (!open) handleClose()
      }}
      width={800}
      maxHeight={isMobile ? 'calc(100dvh - 64px)' : undefined}
      purpose="form"
      className="application-settings-dialog"
    >
      <Layout
        className="settings-dialog-layout"
        header={
          <DialogHeader
            title="Application Settings"
            onOpenChange={(open) => {
              if (!open) handleClose()
            }}
          />
        }
        start={
          <LayoutPanel
            width={220}
            padding={3}
            hasDivider
            role="navigation"
            label="Settings sections"
          >
            <SettingsSectionNav
              label="Settings sections"
              activeId={activeSection}
              sections={[
                { id: 'account' as const, label: 'Account', icon: <UserRound size={16} /> },
                { id: 'profile' as const, label: 'Profile', icon: <UserRound size={16} /> },
                {
                  id: 'model' as const,
                  label: 'Model configuration',
                  icon: <MonitorCog size={16} />
                },
                { id: 'appearance' as const, label: 'Appearance', icon: <Palette size={16} /> }
              ]}
              onSelect={setActiveSection}
            />
          </LayoutPanel>
        }
        content={
          <LayoutContent padding={6}>
            <VStack gap={6}>
              <VStack gap={1}>
                <Heading level={2}>{sectionDetails.title}</Heading>
                <p className="settings-section-description">{sectionDetails.description}</p>
              </VStack>

              {activeSection === 'account' && (
                <FormLayout direction="vertical">
                  <TextInput
                    label="Name"
                    value={displayName}
                    onChange={setDisplayName}
                    description="Your account name."
                  />
                  <Text as="p" type="supporting">
                    Your publishing identity is managed under{' '}
                    <Link onClick={() => setActiveSection('profile')} hasUnderline>
                      Profile
                    </Link>
                    .
                  </Text>
                  <TextInput
                    label="Email address"
                    type="email"
                    value={session?.user.email || ''}
                    onChange={() => undefined}
                    isDisabled
                    disabledMessage="Email changes are not supported yet."
                  />
                  <TextInput
                    label="Current password"
                    type="password"
                    value={currentPassword}
                    onChange={setCurrentPassword}
                    description="Leave the password fields empty if you only want to update your name."
                  />
                  <TextInput
                    label="New password"
                    type="password"
                    value={newPassword}
                    onChange={setNewPassword}
                  />
                  <TextInput
                    label="Confirm new password"
                    type="password"
                    value={confirmPassword}
                    onChange={setConfirmPassword}
                  />
                </FormLayout>
              )}

              {activeSection === 'profile' && (
                <FormLayout direction="vertical">
                  <HStack gap={4} align="center">
                    <button
                      type="button"
                      className="settings-avatar-button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={avatarBusy}
                      aria-label="Change profile picture"
                    >
                      <Avatar
                        size='xl'
                        src={profile?.avatarUrl ?? DEFAULT_AVATAR_IMAGE}
                        name={penName || displayName}
                      />
                    </button>
                    <VStack gap={2}>
                      <HStack gap={2}>
                        <PillButton
                          variant="secondary"
                          label="Upload photo"
                          isLoading={avatarBusy}
                          onClick={() => fileInputRef.current?.click()}
                        />
                        {hasDefaultAvatars && (
                          <PillButton
                            variant="secondary"
                            label="Choose avatar"
                            isDisabled={avatarBusy}
                            onClick={() => setPickerOpen(true)}
                          />
                        )}
                        {profile?.avatarUrl && (
                          <PillButton
                            variant="ghost"
                            label="Remove"
                            isDisabled={avatarBusy}
                            onClick={() => void handleAvatarRemove()}
                          />
                        )}
                      </HStack>
                      <Text as="p" type="supporting">
                        JPEG, PNG, WebP, or GIF. Up to 5MB.
                      </Text>
                    </VStack>
                    {/* The `cancel` event this input fires is intercepted in an effect above —
                        see the comment there. It cannot be handled with an onCancel prop
                        because React does not type `cancel` for input elements. */}
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={(event) => void handleAvatarFile(event)}
                      hidden
                    />
                  </HStack>
                  <TextInput
                    label="Pen name"
                    value={penName}
                    onChange={setPenName}
                    description="The author name shown on your published stories."
                    isRequired
                  />
                  <TextArea
                    label="Author blurb"
                    value={authorBlurb}
                    onChange={setAuthorBlurb}
                    description="An optional short biography for your author profile."
                    isOptional
                    maxLength={1200}
                  />
                  <Switch
                    label="Show my library on my profile"
                    description="Everyone can see the stories you have saved. Turn this off to keep them to yourself."
                    value={libraryPublic}
                    onChange={setLibraryPublic}
                  />
                </FormLayout>
              )}

              {activeSection === 'model' && (
                <FormLayout direction="vertical">
                  <Selector
                    label="Active LLM model"
                    value={localSettings.selectedModel}
                    options={modelSelectorOptions(models)}
                    onChange={(value: string) =>
                      setLocalSettings((previous) => ({ ...previous, selectedModel: value }))
                    }
                    isDisabled={models.length === 0}
                    placeholder={models.length === 0 ? 'No models available' : 'Select a model'}
                  />
                  <Slider
                    label="Creativity (temperature)"
                    min={0}
                    max={1.5}
                    step={0.1}
                    value={localSettings.temperature ?? 0.6}
                    onChange={(value: number) =>
                      setLocalSettings((previous) => ({ ...previous, temperature: value }))
                    }
                    description={`Current: ${localSettings.temperature ?? 0.6}. Lower values are more focused; higher values are more exploratory.`}
                  />
                </FormLayout>
              )}

              {activeSection === 'appearance' && (
                <FormLayout direction="vertical">
                  <SegmentedControl
                    label="Theme"
                    value={localSettings.theme}
                    onChange={(value) => {
                      const theme = value as AppSettings['theme']
                      setLocalSettings((previous) => ({ ...previous, theme }))
                      applyTheme(theme)
                    }}
                    layout="fill"
                  >
                    <SegmentedControlItem value="light" label="Light" />
                    <SegmentedControlItem value="dark" label="Dark" />
                    <SegmentedControlItem value="system" label="System" />
                  </SegmentedControl>
                </FormLayout>
              )}

              {feedback && (
                <HStack
                  gap={2}
                  className={cn('settings-feedback', `settings-feedback-${feedback.status}`)}
                >
                  {feedback.status === 'success' ? (
                    <CheckCircle size={16} />
                  ) : (
                    <AlertTriangle size={16} />
                  )}
                  <span>{feedback.message}</span>
                </HStack>
              )}
            </VStack>
          </LayoutContent>
        }
        footer={
          <LayoutFooter hasDivider>
            <HStack gap={2} hAlign="end">
              <PillButton label="Close" variant="secondary" onClick={handleClose} />
              <PillButton
                label={
                  activeSection === 'account'
                    ? 'Save account'
                    : activeSection === 'profile'
                      ? 'Save profile'
                      : 'Save changes'
                }
                variant="primary"
                clickAction={handleSave}
                isLoading={saving}
              />
            </HStack>
          </LayoutFooter>
        }
      />

      <DefaultAvatarPicker
        isOpen={pickerOpen}
        currentAvatarUrl={profile?.avatarUrl ?? null}
        onSelect={async (defaultAvatarId) => {
          await onSelectDefaultAvatar(defaultAvatarId)
          setFeedback({ status: 'success', message: 'Profile picture updated.' })
        }}
        onClose={() => setPickerOpen(false)}
      />
    </Dialog>
  );
}
